import useSWR from 'swr'
import useSWRImmutable from 'swr/immutable'

import config from 'config'
import {useUser} from '~/shell/user'

import {getDeimos, putDeimos} from './helpers'

import {request} from '../api'
import type {
    BillingResponse,
    PlanResponse,
    SubscriptionResponse,
    UpdateStripeSubscriptionRequest,
    UpdateBillingRequest,
} from '../types/billing'

export async function updateBillingBody(companyId?: string, body?: UpdateBillingRequest) {
    return putDeimos(`/rest/v1/billing/companies/${companyId}`, body)
}

export function useBilling(companyId?: string) {
    const user = useUser()
    const result = useSWR<BillingResponse, Error>(
        companyId || user?.companyId
            ? `/rest/v1/billing/companies/${companyId ? companyId : user?.companyId}`
            : null,
        getDeimos,
        {
            shouldRetryOnError: false,
            revalidateOnFocus: false,
        },
    )

    async function updateBilling(body: UpdateBillingRequest) {
        await updateBillingBody(companyId, body)
        await result.mutate()
    }

    return {...result, mutations: {updateBilling}}
}

export function usePlans(companyId = '') {
    const user = useUser()

    return useSWRImmutable<PlanResponse[], Error>(
        `/rest/v1/billing/plans?company-id=${companyId ? companyId : user?.companyId}`,
        getDeimos,
        {
            revalidateOnFocus: false,
        },
    )
}

export function useSubscription(companyId = '', retry = false) {
    const user = useUser()
    const result = useSWR<SubscriptionResponse>(
        `/rest/v1/billing/subscriptions/${companyId ? companyId : user?.companyId}`,
        getDeimos,
        {
            shouldRetryOnError: retry,
            revalidateOnFocus: false,
        },
    )

    async function updateSubscription(payload: UpdateStripeSubscriptionRequest) {
        await putDeimos(`/rest/v1/billing/subscriptions/${payload.companyId}`, payload)
        await result.mutate()
    }

    return {...result, mutations: {updateSubscription}}
}

export function useUpcomingBillingChanges(companyId: string) {
    return useSWR(
        `/rest/v1/billing/subscriptions/${companyId}/upcoming-billing-change`,
        getDeimos,
        {
            revalidateOnFocus: false,
        },
    )
}

export function useBillingAddons(companyId: string | null, country: string) {
    return useSWR<PlanResponse[]>(
        `/rest/v1/billing/plans?country=${country}&filter=addon&company-id=${companyId}`,
        getDeimos,
        {
            revalidateOnFocus: false,
        },
    )
}

export function useBillingInvoices(companyId: string, includeZeroInvoices: boolean) {
    return useSWR<any>(
        `/rest/v1/billing/invoices/companies/${companyId}${
            includeZeroInvoices ? '?filterZero=true' : ''
        }`,
        getDeimos,
        {
            revalidateOnFocus: false,
        },
    )
}

export * from '../types/billing'

export async function calculatePrice(
    companyId: string,
    quantity: number,
    employeeIds: (string | null)[] | null,
) {
    return request(`${config.endpoints.api}/rest/v1/billing/orders/cards/${companyId}`, {
        auth: 'user',
        method: 'POST',
        body: {quantity, employeeIds},
    })
}
